import { FC, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { Button } from '@shared/components/Button';
import ResizableBox from '@shared/components/ResizableBox';
import { Icon } from '@shared/components/icons';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { unsecuredCopyToClipboard } from '@shared/utils/clipboard.util';

const DURATION = 0.3;

interface Props {
  cashAccount?: CashAccount;
  additionalCashAccountList?: CashAccount[];
  forMember?: boolean;
  cashAccountEditOpenId?: string;
}

export const MemberMoney: FC<Props> = function MemberMoney({
  cashAccount,
  forMember,
  cashAccountEditOpenId,
  additionalCashAccountList,
}: Props) {
  const { ChevronDown, ChevronUp, MoneyBag } = Icon();
  const [fold, setFold] = useState(true);
  const setCashAccountEditOpen = useSetRecoilState(
    simpleOpenAtomFamily(cashAccountEditOpenId),
  );
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const cashAccountContent = useMemo(() => {
    if (cashAccount && cashAccount.isVerified) {
      return (
        <div className="flex flex-col gap-2 text-15 font-medium">
          <div className="relative flex justify-between gap-1">
            <p>{cashAccount.name}</p>
            <p
              className="absolute right-0 top-0 cursor-pointer rounded-md border bg-white px-2 py-0.5 text-[14px] font-bold theme-text-main theme-border-main"
              onClick={async (): Promise<void> => {
                navigator.clipboard
                  .writeText(`${cashAccount.bank} ${cashAccount.account}`)
                  .then(() => {
                    notificateSuccess(notifiacationInstance, '계좌복사 완료');
                  })
                  .catch(() => {
                    unsecuredCopyToClipboard(
                      `${cashAccount.bank} ${cashAccount.account}`,
                      '계좌복사 완료',
                      notifiacationInstance,
                    );
                  });
              }}
            >
              계좌복사
            </p>
          </div>
          <div className="flex gap-5">
            <p className="break-keep">{cashAccount.bank}</p>
            <p className="break-keep">{cashAccount.account}</p>
          </div>
        </div>
      );
    } else {
      return <div>인증된 계좌정보가 존재하지 않습니다.</div>;
    }
  }, [cashAccount, notifiacationInstance]);

  const renderAdditionalCashAccount = (account: CashAccount) => {
    if (account.isVerified) {
      return (
        <div
          key={account._id}
          className="mt-3 flex flex-col gap-2 border-t pt-3 text-15 font-medium"
        >
          <div className="relative flex justify-between gap-1">
            <p>{account.name}</p>
            <p
              className="absolute right-0 top-0 cursor-pointer rounded-md border bg-white px-2 py-0.5 text-[14px] font-bold theme-text-main theme-border-main"
              onClick={async (): Promise<void> => {
                navigator.clipboard
                  .writeText(`${account.bank} ${account.account}`)
                  .then(() => {
                    notificateSuccess(notifiacationInstance, '계좌복사 완료');
                  })
                  .catch(() => {
                    unsecuredCopyToClipboard(
                      `${account.bank} ${account.account}`,
                      '계좌복사 완료',
                      notifiacationInstance,
                    );
                  });
              }}
            >
              계좌복사
            </p>
          </div>
          <div className="flex gap-5">
            <p>{account.bank}</p>

            <p>{account.account}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  const memberCashAccountContent = useMemo(() => {
    if (forMember) {
      return (
        <div className="border-t pt-3">
          <div className="center-box">
            <Button
              className="button-no-border p-2 theme-text-3 theme-bg-main"
              onClick={() => {
                setCashAccountEditOpen(true);
              }}
            >
              부의금정보수정
            </Button>
          </div>
        </div>
      );
    }
  }, [forMember, setCashAccountEditOpen]);

  return (
    <div className="bg-white px-2">
      <div
        className={`flex items-center justify-between py-2 ${
          fold ? 'cursor-pointer' : ''
        }`}
        onClick={() => {
          if (fold) {
            setFold(!fold);
          }
        }}
      >
        <div className="center-box">
          <MoneyBag className="wh-10" />
          <p className="font-bold text-gray-500">부의금 안내</p>
        </div>
        <div
          className="cursor-pointer"
          onClick={() => {
            setFold(!fold);
          }}
        >
          {fold ? <ChevronDown /> : <ChevronUp />}
        </div>
      </div>
      <ResizableBox duration={DURATION}>
        {!fold && (
          <div className="space-y-2 border-gray-300 px-4 pb-3">
            {cashAccountContent}
            {additionalCashAccountList?.map(renderAdditionalCashAccount)}
            {memberCashAccountContent}
          </div>
        )}
      </ResizableBox>
    </div>
  );
};
